
import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { FaEdit, FaTrash } from 'react-icons/fa';
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../loader/Loader';
import Select from "react-select";

export default function Bom() {

  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [productList, setProductList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const [units, setUnits] = useState([]);

  const initialState = {
    product_id: '',
    material_id: '',
    quantity: '',
    min_quantity: '',
    max_quantity: '',
    required_quantity: '',
    unit: '',
    status: '',
    vendor: '',
    cost: '',
    remarks: '',
    approved_at: ''
  };

  const [form, setForm] = useState(initialState);

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    fetchList();
    fetchMaterialList();
    fetchProductList();
    fetchUnits();
  }, []);

  // =========================================================
  // FETCH BOM LIST
  // =========================================================

  const fetchList = async () => {
    const token = getAuthToken();

    if (!token) return;

    setListLoading(true);

    try {
      const res = await axios.get(`${config.API}/bom/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setDataList(res.data.boms || []);

    } catch (err) {
      console.error("Error fetching BOM list:", err);
      toast.error("Failed to load BOM data");
    } finally {
      setListLoading(false);
    }
  };

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================

  const fetchProductList = async () => {
    const token = getAuthToken();

    if (!token) return;

    try {
      const res = await axios.get(`${config.API}/product/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setProductList(res.data.products || []);

    } catch (err) {
      console.error("Error fetchProductList:", err);
      toast.error("Failed to load products");
    }
  };

  // =========================================================
  // FETCH MATERIALS
  // =========================================================

  const fetchMaterialList = async () => {
    const token = getAuthToken();

    if (!token) return;

    try {
      const res = await axios.get(`${config.API}/material/master/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setMaterialList(res.data.materials || []);

    } catch (err) {
      console.error("fetchMaterialList:", err);
      toast.error("Failed to load materials");
    }
  };

  // =========================================================
  // FETCH UNITS
  // =========================================================

  const fetchUnits = async () => {

    try {

      const res = await axios.post(
        `${config.API}/getUnit`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`
          }
        }
      );

      setUnits(res.data || []);

    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  // =========================================================
  // FORM INPUT
  // =========================================================

  const handleInput = (e) => {

    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    setErrors(prev => ({
      ...prev,
      [name]: null
    }));
  };

  // =========================================================
  // ADD
  // =========================================================

  const openAddModal = () => {

    setForm(initialState);
    setErrors({});
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };

  // =========================================================
  // EDIT
  // =========================================================

  const openEditModal = (item) => {

    setForm({
      ...item,

      product_id: item.product_id ?? '',
      material_id: item.material_id ?? '',

      approved_at: item.approved_at
        ? item.approved_at.replace(' ', 'T')
        : ''
    });

    setErrors({});
    setIsEditing(true);
    setEditingId(item.id);
    setShowModal(true);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (id) => {

    if (!window.confirm("Are you sure you want to delete this BOM?")) {
      return;
    }

    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    try {

      await axios.delete(`${config.API}/bom/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("Deleted successfully");

      fetchList();

    } catch (err) {

      console.error("Delete error:", err);

      toast.error(
        err.response?.data?.message ||
        "Failed to delete BOM"
      );
    }
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {

    const token = getAuthToken();

    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    setErrors({});

    const payload = {

      product_id: Number(form.product_id),

      material_id: Number(form.material_id),

      quantity: Number(form.quantity),

      min_quantity: Number(form.min_quantity),

      max_quantity: Number(form.max_quantity),

      required_quantity: Number(form.required_quantity),

      unit: form.unit,

      status: form.status,

      vendor: form.vendor,

      cost: Number(form.cost),

      remarks: form.remarks,

      approved_at: form.approved_at
        ? form.approved_at.replace('T', ' ')
        : null
    };

    setFormLoading(true);

    try {

      if (isEditing && editingId) {

        await axios.put(
          `${config.API}/bom/${editingId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        toast.success("Updated successfully");

      } else {

        const response = await axios.post(
          `${config.API}/bom/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        toast.success(
          response.data.message || "Created successfully"
        );
      }

      setShowModal(false);

      fetchList();

    } catch (err) {

      if (
        err.response?.status === 422 &&
        err.response?.data?.errors
      ) {

        setErrors(err.response.data.errors);

      } else {

        console.error("Submit error:", err);

        toast.error(
          err.response?.data?.message ||
          "Submit failed! Please try again."
        );
      }

    } finally {

      setFormLoading(false);
    }
  };

  // =========================================================
  // FILTER
  // =========================================================

  const filtered = dataList.filter(item => {

    const productName =
      item.product?.name || '';

    const materialName =
      item.material?.material_name || '';

    const searchString = [
      item.id,
      productName,
      materialName,
      item.quantity,
      item.min_quantity,
      item.max_quantity,
      item.required_quantity,
      item.unit,
      item.status,
      item.vendor,
      item.cost,
      item.remarks
    ]
      .join(' ')
      .toLowerCase();

    return searchString.includes(
      filterText.toLowerCase()
    );
  });

  // =========================================================
  // MUI DATA GRID COLUMNS
  // =========================================================

const columns = [
  {
    field: 'id',
    headerName: 'ID',
    width: 70,
  },

  {
    field: 'product_name',
    headerName: 'Product',
    minWidth: 220,
    flex: 1,
    valueGetter: (value, row) =>
      row.product?.name || '',
  },

  {
    field: 'material_name',
    headerName: 'Material',
    minWidth: 160,
    flex: 1,
    valueGetter: (value, row) =>
      row.material?.material_name || '',
  },

  //{
   // field: 'quantity',
   // headerName: 'Quantity',
   // width: 100,
   // type: 'number',
  //},

  {
    field: 'min_quantity',
    headerName: 'Min Qty',
    width: 100,
    type: 'number',
  },

  {
    field: 'max_quantity',
    headerName: 'Max Qty',
    width: 100,
    type: 'number',
  },

  {
    field: 'required_quantity',
    headerName: 'Required Qty',
    width: 110,
    type: 'number',
  },

  {
    field: 'unit',
    headerName: 'Unit',
    width: 100,
  },

  {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    filterable: false,

    renderCell: (params) => (
      <div
        className="d-flex gap-2"
        style={{
          height: '100%',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => openEditModal(params.row)}
          title="Edit"
        >
          <FaEdit />
        </button>

        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={() => handleDelete(params.row.id)}
          title="Delete"
        >
          <FaTrash />
        </button>
      </div>
    ),
  },
];
  // =========================================================
  // SELECT OPTIONS
  // =========================================================

  const productOptions = productList.map((p) => ({
    value: p.id,
    label: p.name
  }));

  const materialOptions = materialList.map((m) => ({
    value: m.id,
    label: m.material_name
  }));

  // =========================================================
  // RETURN
  // =========================================================

  return (

    <div className="container-fluid mt-3">

      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="d-flex justify-content-between align-items-center mb-3">

        <strong className="mb-0 fs-4">
          BOM Details
        </strong>

        <div className="d-flex gap-2">

          <input
            type="text"
            className="form-control"
            placeholder="Search BOM..."
            value={filterText}
            onChange={(e) =>
              setFilterText(e.target.value)
            }
            style={{
              width: '250px'
            }}
          />

          <button
            className="btn btn-primary"
            onClick={openAddModal}
          >
            + Add BOM
          </button>

        </div>

      </div>

      {/* =====================================================
          DATA GRID
      ====================================================== */}

      {listLoading ? (

        <div className="text-center py-5">
          <Loader />
        </div>

      ) : (

        <div
          style={{
            width: '100%',
            backgroundColor: '#fff'
          }}
        >

 <DataGrid
  rows={filtered}
  columns={columns}
  getRowId={(row) => row.id}
  autoHeight

  pagination

  pageSizeOptions={[10, 25, 50, 100]}

  initialState={{
    pagination: {
      paginationModel: {
        pageSize: 10,
        page: 0,
      },
    },
  }}

  disableRowSelectionOnClick

  sx={{
    width: '100%',
    border: '1px solid #dee2e6',

    // HEADER
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#0d6efd !important',
      color: '#ffffff !important',
      minHeight: '52px !important',
      maxHeight: '52px !important',
    },

    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#0d6efd !important',
      color: '#ffffff !important',
      minHeight: '52px !important',
      maxHeight: '52px !important',
    },

    // HEADER LABEL
    '& .MuiDataGrid-columnHeaderTitle': {
      color: '#ffffff !important',
      fontWeight: '700 !important',
      fontSize: '14px',
      whiteSpace: 'normal',
    },

    // SORT ICON
    '& .MuiDataGrid-iconButtonContainer': {
      color: '#ffffff !important',
    },

    '& .MuiDataGrid-sortIcon': {
      color: '#ffffff !important',
    },

    // MENU ICON
    '& .MuiDataGrid-menuIconButton': {
      color: '#ffffff !important',
    },

    // ROWS
    '& .MuiDataGrid-row:hover': {
      backgroundColor: '#f5f9ff',
    },

    '& .MuiDataGrid-cell': {
      fontSize: '14px',
    },

    // FOOTER
    '& .MuiDataGrid-footerContainer': {
      borderTop: '1px solid #dee2e6',
    },
  }}
/>

        </div>

      )}

      {/* =====================================================
          MODAL
      ====================================================== */}

      {showModal && (

        <div
          className="modal d-block"
          style={{
            background: 'rgba(0,0,0,0.5)'
          }}
        >

          <div className="modal-dialog modal-lg modal-dialog-centered">

            <div className="modal-content">

              {/* HEADER */}

              <div className="modal-header bg-primary text-light">

                <h5 className="mb-0">

                  {isEditing
                    ? 'Edit BOM'
                    : 'Add BOM'}

                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() =>
                    setShowModal(false)
                  }
                  disabled={formLoading}
                />

              </div>

              {/* BODY */}

              <div className="modal-body">

                <div className="row g-3">

                  {/* PRODUCT */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Product
                    </label>

                    <Select
                      name="product_id"

                      value={
                        productOptions.find(
                          option =>
                            option.value ===
                            form.product_id
                        ) || null
                      }

                      onChange={(selectedOption) => {

                        setForm(prev => ({
                          ...prev,

                          product_id:
                            selectedOption
                              ? selectedOption.value
                              : ""
                        }));

                        setErrors(prev => ({
                          ...prev,
                          product_id: null
                        }));
                      }}

                      options={productOptions}

                      isDisabled={
                        formLoading
                      }

                      placeholder="-- Select Product --"

                      isSearchable

                      styles={{
                        control: (base) => ({
                          ...base,

                          borderColor:
                            errors.product_id
                              ? "red"
                              : base.borderColor
                        })
                      }}
                    />

                    {errors.product_id && (

                      <div className="text-danger small mt-1">

                        {errors.product_id[0]}

                      </div>

                    )}

                  </div>

                  {/* MATERIAL */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Material
                    </label>

                    <Select
                      name="material_id"

                      value={
                        materialOptions.find(
                          option =>
                            option.value ===
                            form.material_id
                        ) || null
                      }

                      onChange={(selectedOption) => {

                        setForm(prev => ({
                          ...prev,

                          material_id:
                            selectedOption
                              ? selectedOption.value
                              : ""
                        }));

                        setErrors(prev => ({
                          ...prev,
                          material_id: null
                        }));
                      }}

                      options={materialOptions}

                      isDisabled={
                        formLoading
                      }

                      placeholder="-- Select Material --"

                      isSearchable

                      styles={{
                        control: (base) => ({
                          ...base,

                          borderColor:
                            errors.material_id
                              ? "red"
                              : base.borderColor
                        })
                      }}
                    />

                    {errors.material_id && (

                      <div className="text-danger small mt-1">

                        {errors.material_id[0]}

                      </div>

                    )}

                  </div>

                  {/* QUANTITY */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Quantity
                    </label>

                    <input
                      type="number"
                      name="quantity"
                      className="form-control"
                      value={form.quantity}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                    {errors.quantity && (

                      <div className="text-danger small">

                        {errors.quantity[0]}

                      </div>

                    )}

                  </div>

                  {/* MIN */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Min Quantity
                    </label>

                    <input
                      type="number"
                      name="min_quantity"
                      className="form-control"
                      value={form.min_quantity}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* MAX */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Max Quantity
                    </label>

                    <input
                      type="number"
                      name="max_quantity"
                      className="form-control"
                      value={form.max_quantity}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* REQUIRED */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Required Quantity
                    </label>

                    <input
                      type="number"
                      name="required_quantity"
                      className="form-control"
                      value={form.required_quantity}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* UNIT */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Unit
                    </label>

                    <select
                      name="unit"
                      className="form-select"
                      value={form.unit}
                      onChange={handleInput}
                      disabled={formLoading}
                    >

                      <option value="">
                        Select Unit
                      </option>

                      {units.map((unit) => (

                        <option
                          key={unit.id}
                          value={unit.name}
                        >
                          {unit.name}
                        </option>

                      ))}

                    </select>

                  </div>

                  {/* STATUS */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Status
                    </label>

                    <input
                      type="text"
                      name="status"
                      className="form-control"
                      value={form.status}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                    {errors.status && (

                      <div className="text-danger small">

                        {errors.status[0]}

                      </div>

                    )}

                  </div>

                  {/* VENDOR */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Vendor
                    </label>

                    <input
                      type="text"
                      name="vendor"
                      className="form-control"
                      value={form.vendor}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* COST */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Cost
                    </label>

                    <input
                      type="number"
                      name="cost"
                      className="form-control"
                      value={form.cost}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* REMARKS */}

                  <div className="col-md-8">

                    <label className="form-label">
                      Remarks
                    </label>

                    <input
                      type="text"
                      name="remarks"
                      className="form-control"
                      value={form.remarks}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                  {/* APPROVED */}

                  <div className="col-md-4">

                    <label className="form-label">
                      Approved At
                    </label>

                    <input
                      type="datetime-local"
                      name="approved_at"
                      className="form-control"
                      value={form.approved_at}
                      onChange={handleInput}
                      disabled={formLoading}
                    />

                  </div>

                </div>

              </div>

              {/* FOOTER */}

              <div className="modal-footer">

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                  disabled={formLoading}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={formLoading}
                >

                  {formLoading
                    ? 'Saving...'
                    : isEditing
                      ? 'Update'
                      : 'Save'}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}
